import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { meApi } from '../../lib/api/me';
import { ApiError } from '../../lib/http';
import type { User } from '../../lib/types';

type AuthContextValue = {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const ME_QUERY_KEY = ['me'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => meApi.show().then((res) => res.data),
    retry: (failureCount, error) => (error instanceof ApiError && error.status === 401 ? false : failureCount < 2),
    staleTime: 60_000,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user: query.data ?? null,
      isLoading: query.isLoading,
      isAuthenticated: Boolean(query.data),
    }),
    [query.data, query.isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useInvalidateAuth() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}
